import { type ApiClient, createApiClient } from '@hostesa/api-client';
import {
  createMemorySessionStore,
  createWebStorageSessionStore,
  type SessionStore,
} from '@hostesa/auth-client';
import { type AppConfig, loadAppConfig } from '@hostesa/config';
import { createFlagEvaluator, type FlagEvaluator } from '@hostesa/feature-flags';
import { initObservability } from '@hostesa/observability';
import { QueryClient } from '@tanstack/react-query';

/**
 * App-wide singletons, constructed once at bootstrap.
 *
 * Order matters:
 *  1. Validate env (`loadAppConfig`) — fails fast on a missing/bad variable.
 *  2. `initObservability` — must run before any route renders (Sentry release).
 *  3. Session store (sessionStorage-backed so the session survives a page
 *     refresh; falls back to in-memory when Storage is unavailable, e.g. SSR
 *     or a locked-down browser). Never uses localStorage — see auth-client
 *     session.ts for the XSS trade-off rationale.
 *  4. API client — Bearer token from the session store; a 401 clears the
 *     session and routes to /login (wired by the caller via onUnauthorized).
 *  5. QueryClient + feature-flag evaluator.
 */

export interface AppRuntime {
  readonly config: AppConfig;
  readonly sessionStore: SessionStore;
  readonly apiClient: ApiClient;
  readonly queryClient: QueryClient;
  readonly flagEvaluator: FlagEvaluator;
}

export interface BootstrapOptions {
  /** Called when any API call returns 401 — redirect the user to /login. */
  onUnauthorized: () => void;
}

export function bootstrapApp({ onUnauthorized }: BootstrapOptions): AppRuntime {
  const config = loadAppConfig(import.meta.env);

  initObservability({
    app: 'platform',
    environment: config.environment,
    release: `platform@${config.releaseId}`,
    releaseId: config.releaseId,
    // Sentry is a no-op without a DSN; an empty string keeps local/dev quiet.
    dsn: config.sentryDsn ?? '',
  });

  const sessionStore =
    typeof window !== 'undefined' && window.sessionStorage
      ? createWebStorageSessionStore(window.sessionStorage)
      : createMemorySessionStore();

  const apiClient = createApiClient({
    baseUrl: config.apiBaseUrl,
    getToken: () => sessionStore.getToken(),
    onUnauthorized: () => {
      // Clear the (now-invalid) session before redirecting so guards see a
      // logged-out state immediately.
      sessionStore.clear();
      onUnauthorized();
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // REST is the source of truth; realtime invalidates on top of this.
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Never retry auth/permission/not-found — they are not transient.
          const status = (error as { status?: number } | null)?.status;
          if (status === 401 || status === 403 || status === 404) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
      },
    },
  });

  // Per-hotel overrides are resolved later from the hotel context (gap: the
  // backend does not yet expose per-hotel flag overrides — see slug-resolution).
  const flagEvaluator = createFlagEvaluator({
    environment:
      config.environment === 'production' ? {} : { backoffice_provider_diagnostics: false },
  });

  return { config, sessionStore, apiClient, queryClient, flagEvaluator };
}
